/// Live ADS-B traffic classifier — TypeScript port of the validated N90
/// reference at iOS `docs/adsb-live-traffic/classify.py` (~90% sensible
/// single-shot vs `n90_sample.json`). Pure geometry + broadcast state; no
/// external route lookups. Mirrors the iOS `TrafficClassifier`.

export type FlightPhase =
  | 'ground'
  | 'vfr_local'
  | 'departure'
  | 'arrival'
  | 'overflight'
  | 'level_low'
  | 'unknown'

/// The subset of adsb.lol `/v2/point` aircraft fields the classifier reads.
export interface AdsbAircraft {
  hex: string
  flight?: string
  r?: string
  t?: string
  category?: string
  lat?: number
  lon?: number
  alt_baro?: number | 'ground'
  gs?: number
  track?: number
  baro_rate?: number
  geom_rate?: number
  nav_altitude_mcp?: number
  nav_altitude_fms?: number
  squawk?: string
  emergency?: string
  seen_pos?: number
}

export interface RegionAirport {
  icao: string
  lat: number
  lon: number
  /// Both directions of every runway, in TRUE degrees (NYC ≈ magnetic − 13°W).
  runwayTrueHeadings: number[]
}

/// N90 (New York TRACON) — the 7 fields the classifier scores over. The level
/// itself only renders the Core-4 (JFK/LGA/EWR/TEB); the 3 satellites
/// (HPN/FRG/ISP) improve assignment accuracy and spawn as pass-through traffic.
export const N90_AIRPORTS: readonly RegionAirport[] = [
  { icao: 'KJFK', lat: 40.6398, lon: -73.7789, runwayTrueHeadings: [30, 210, 108, 288] },
  { icao: 'KLGA', lat: 40.7772, lon: -73.8726, runwayTrueHeadings: [31, 211, 112, 292] },
  { icao: 'KEWR', lat: 40.6925, lon: -74.1687, runwayTrueHeadings: [30, 210, 98, 278] },
  { icao: 'KTEB', lat: 40.8501, lon: -74.0608, runwayTrueHeadings: [6, 186, 47, 227] },
  { icao: 'KHPN', lat: 41.0670, lon: -73.7076, runwayTrueHeadings: [147, 327, 98, 278] },
  { icao: 'KFRG', lat: 40.7288, lon: -73.4134, runwayTrueHeadings: [127, 307, 6, 186] },
  { icao: 'KISP', lat: 40.7952, lon: -73.1002, runwayTrueHeadings: [47, 227, 137, 317] },
]

const MAJORS = new Set(['KJFK', 'KLGA', 'KEWR'])
const BIZJET_TYPES = new Set([
  'GLF6', 'GLF5', 'GLF4', 'G280', 'GL7T', 'GLEX', 'C650', 'C700', 'C68A',
  'CL35', 'CL30', 'CL60', 'E55P', 'E545', 'F2TH', 'FA7X', 'FA8X', 'PC24',
  'C25A', 'C25B', 'C25C', 'C56X', 'C560', 'C680', 'LJ60', 'LJ75', 'H25B',
])
const WIDEBODY_TYPES = new Set(['B748', 'B77W', 'B772', 'B789', 'B788', 'A388', 'A35K'])

export function haversineNM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3440.065
  const p1 = (lat1 * Math.PI) / 180
  const p2 = (lat2 * Math.PI) / 180
  const dp = ((lat2 - lat1) * Math.PI) / 180
  const dl = ((lon2 - lon1) * Math.PI) / 180
  const a = Math.sin(dp / 2) ** 2 + Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2
  return 2 * R * Math.asin(Math.sqrt(a))
}

export function bearing(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const p1 = (lat1 * Math.PI) / 180
  const p2 = (lat2 * Math.PI) / 180
  const dl = ((lon2 - lon1) * Math.PI) / 180
  const x = Math.sin(dl) * Math.cos(p2)
  const y = Math.cos(p1) * Math.sin(p2) - Math.sin(p1) * Math.cos(p2) * Math.cos(dl)
  return ((Math.atan2(x, y) * 180) / Math.PI + 360) % 360
}

export function angDiff(a: number, b: number): number {
  return Math.abs((((a - b + 180) % 360) + 360) % 360 - 180)
}

const altFeet = (ac: AdsbAircraft): number | null =>
  typeof ac.alt_baro === 'number' ? ac.alt_baro : null
const fmsIntent = (ac: AdsbAircraft): number | null =>
  ac.nav_altitude_fms ?? ac.nav_altitude_mcp ?? null

export function classifyPhase(ac: AdsbAircraft): FlightPhase {
  if (ac.alt_baro === 'ground') return 'ground'
  const alt = altFeet(ac)
  if (alt == null) return 'unknown'
  const rate = ac.baro_rate ?? null
  const fms = fmsIntent(ac)
  const gs = ac.gs ?? 0
  const cat = ac.category ?? ''
  // VFR / GA puttering: low, slow, no FMS intent, not descending, not an airliner.
  if (alt < 3500 && gs < 140 && fms == null && !['A3', 'A4', 'A5'].includes(cat) &&
      !(rate != null && rate < -300)) {
    return 'vfr_local'
  }
  if (rate != null && rate > 400) return 'departure'
  if (rate != null && rate < -400) return 'arrival'
  if (fms != null) {
    if (fms < alt - 700) return 'arrival' // cleared to descend, hasn't started
    if (fms > alt + 700) return 'departure' // step-climbing
  }
  if (alt >= 18000) return 'overflight'
  return fms != null && fms <= 12000 ? 'arrival' : 'level_low'
}

/// Score every airport on geometry; return the winning ICAO (≥2.5) or null.
export function assignAirport(
  ac: AdsbAircraft,
  phase: FlightPhase,
  airports: readonly RegionAirport[] = N90_AIRPORTS,
): string | null {
  const lat = ac.lat
  const lon = ac.lon
  const alt = altFeet(ac)
  if (lat == null || lon == null || alt == null) return null
  const track = ac.track ?? null
  const cat = ac.category ?? ''
  const typ = ac.t ?? ''
  let best: string | null = null
  let bestScore = 0

  for (const apt of airports) {
    const d = haversineNM(lat, lon, apt.lat, apt.lon)
    if (d > 60) continue
    let score = 0

    if (phase === 'arrival') {
      const brgTo = bearing(lat, lon, apt.lat, apt.lon)
      if (track != null) {
        const te = angDiff(track, brgTo)
        if (te < 30) score += 3
        else if (te < 60) score += 1.5
        else if (d < 12) score += 1 // close-in maneuvering
        else if (te > 120 && d > 12) continue // flying away from a distant field
      }
      const expected = d * 318 + 1200 // ~300 ft/nm + terminal pad
      const ratio = expected ? alt / expected : 9
      if (ratio > 0.35 && ratio < 1.7) score += 2
      else if (ratio >= 2.5) continue // way too high for this field
      if (track != null && alt < 6000) {
        const minOff = Math.min(...apt.runwayTrueHeadings.map((r) => angDiff(track, r)))
        if (minOff < 18) score += 2 // runway alignment on short final
      }
      score += Math.max(0, (60 - d) / 60)
    } else if (phase === 'departure') {
      const brgFrom = bearing(apt.lat, apt.lon, lat, lon)
      if (track != null) {
        const te = angDiff(track, brgFrom)
        if (te < 35) score += 3
        else if (te < 70) score += 1.5
        else if (d > 10) continue
      }
      const expected = d * 350 + 800 // climbs are steeper
      const ratio = expected ? alt / expected : 9
      if (ratio > 0.3 && ratio < 2.2) score += 2
      else if (ratio > 3.5) continue
      score += Math.max(0, (40 - d) / 40)
    } else {
      continue
    }

    // Aircraft-class vs airport-class tiering.
    if (MAJORS.has(apt.icao)) {
      if (['A3', 'A4', 'A5'].includes(cat)) score += 1.5
      if (BIZJET_TYPES.has(typ)) score -= 1.0
    } else {
      if (cat === 'A5' || WIDEBODY_TYPES.has(typ)) continue // widebodies never at GA fields
      if (['A3', 'A4'].includes(cat)) score -= 2.0
      if (BIZJET_TYPES.has(typ) && ['KTEB', 'KHPN', 'KFRG'].includes(apt.icao)) score += 1.5
    }

    if (score > bestScore) {
      best = apt.icao
      bestScore = score
    }
  }
  return bestScore >= 2.5 ? best : null
}

/// The active landing flow's TRUE heading, inferred from the median track of
/// descending traffic (<2000 ft, ≥200 fpm down) within 12 NM of a major. This
/// is what the real airport is actually landing right now — feed it to the
/// game's wind so the active runway matches live traffic. Returns null when the
/// picture is too quiet to tell (<3 descending samples). Mirrors iOS
/// `TrafficClassifier.inferActiveFlowTrueHeading`.
export function inferActiveFlowTrueHeading(
  aircraft: readonly AdsbAircraft[],
  airports: readonly RegionAirport[] = N90_AIRPORTS,
): number | null {
  const tracks: number[] = []
  for (const ac of aircraft) {
    const alt = altFeet(ac)
    if (alt == null || alt >= 2000) continue
    if (ac.track == null) continue
    const rate = ac.baro_rate ?? ac.geom_rate ?? null
    if (rate == null || rate >= -200) continue
    if (ac.lat == null || ac.lon == null) continue
    const nearMajor = airports.some(
      (a) => MAJORS.has(a.icao) && haversineNM(ac.lat as number, ac.lon as number, a.lat, a.lon) < 12,
    )
    if (nearMajor) tracks.push(ac.track)
  }
  if (tracks.length < 3) return null
  let sx = 0
  let sy = 0
  for (const t of tracks) {
    sx += Math.cos((t * Math.PI) / 180)
    sy += Math.sin((t * Math.PI) / 180)
  }
  return ((Math.atan2(sy, sx) * 180) / Math.PI + 360) % 360 // circular mean
}

/// adsb.lol callsign: trimmed `flight`, else registration, else hex.
export function displayCallsign(ac: AdsbAircraft): string {
  const f = ac.flight?.trim()
  if (f) return f
  if (ac.r) return ac.r
  return ac.hex.toUpperCase()
}
